import { Component, computed, effect, inject, input, output, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { AutoCompleteCompleteEvent } from 'primeng/autocomplete';
import { debounceTime, finalize, merge } from 'rxjs';
import _ from 'lodash';
import { CostCenter } from '../../../models/cost-center/cost-center';
import { ExpenseConcept } from '../../../models/expense-concept/expense-concept';
import {
  ExpenseReceipt,
  ExpenseReceiptSplit,
  ExpenseReceiptStatus,
  ExpenseReceiptUpdate,
  ExpenseReceiptVendor,
  MANUAL_RATE_SOURCE,
} from '../../../models/expense-receipt/expense-receipt';
import {
  ExpenseReceiptForm,
  ExpenseReceiptLineForm,
  ExpenseReceiptSplitForm,
  newReceiptLineForm,
  newReceiptSplitForm,
  receiptMissingFieldLabels,
  splitTax,
  TAX_RATE_OPTIONS,
} from '../../../models/expense-receipt/expense-receipt-form';
import { PostingModule } from '../../../models/financial-account/financial-account';
import { GlAccountRuleAccount, GlAccountRuleRole, GlAccountRuleScope } from '../../../models/gl-account-rule/gl-account-rule';
import { ExchangeRateProxyService } from '../../../proxies/exchange-rate.proxy.service';
import { ExpenseReceiptProxyService } from '../../../proxies/expense-receipt.proxy.service';
import { GlAccountRuleProxyService } from '../../../proxies/gl-account-rule.proxy.service';
import { LocaleHelperService } from '../../../services/locale-helper.service';
import { CURRENCY_OPTIONS, Currency, currencySymbol } from '../../../utilities/currency.util';
import { CostCenterSelection } from '../../shared/cost-center-select/cost-center-select.component';

const ACCEPTED_FILES = '.pdf,.png,.jpg,.jpeg,.xlsx,.docx';

/**
 * - `capture`: the receipt as its capturer enters it (#5794); the GL account column shows what the rules resolved.
 * - `review`: Accounting's review of an approved receipt (#5798). Vendor, date, currency, rate and amounts stay as
 *   approved and lines and cost centers cannot be added or removed; concept, description, tax rate, each cost center
 *   and its GL account, the fiscal number and CAI can change.
 */
export type ReceiptEditorMode = 'capture' | 'review';

/** What the editor hands its host to save. `next` = "Save and next" (capture only). */
export interface ReceiptEditorSave {
  update: ExpenseReceiptUpdate;
  next: boolean;
}

/**
 * The receipt editor shared by receipt capture (#5794) and the expense claim's Accounting review (#5798): header
 * fields, then the lines in a table (concept, description, tax rate, then each cost center the line is split to with
 * its GL account and amount with ISV), then the totals and attachments. The host saves: it receives a ready {@link ExpenseReceiptUpdate} and decides the call.
 */
@Component({
  selector: 'app-expense-receipt-editor',
  templateUrl: './expense-receipt-editor.component.html',
  styleUrl: './expense-receipt-editor.component.scss',
})
export class ExpenseReceiptEditorComponent {
  private readonly proxy = inject(ExpenseReceiptProxyService);
  private readonly ruleProxy = inject(GlAccountRuleProxyService);
  private readonly exchangeRateProxy = inject(ExchangeRateProxyService);
  private readonly locale = inject(LocaleHelperService);
  private readonly translate = inject(TranslateService);

  /** The receipt being edited; null = a new receipt (capture only). */
  readonly receipt = input<ExpenseReceipt | null>(null);
  /** Bumped by the host every time it opens the editor, so reopening the same receipt resets the form. */
  readonly openKey = input(0);
  /** A fund's currency fixes the receipt's; null for a trip (HNL or USD). */
  readonly ownerCurrency = input<Currency | null>(null);
  readonly mode = input<ReceiptEditorMode>('capture');
  readonly concepts = input<ExpenseConcept[]>([]);

  readonly saved = output<ReceiptEditorSave>();
  readonly cancelled = output<void>();
  readonly attach = output<File>();
  readonly removeAttachment = output<string>();

  readonly taxRateOptions = TAX_RATE_OPTIONS;
  readonly accept = ACCEPTED_FILES;
  readonly currencyOptions = CURRENCY_OPTIONS.filter((c) => c.value !== Currency.EUR);
  readonly pettyCashModule = PostingModule.PettyCash;

  readonly vendorSuggestions = signal<ExpenseReceiptVendor[]>([]);

  /**
   * "Refresh GL accounts" (review): what the rules give each cost center of a line for the line's concept, shown
   * without saving — saving resolves them again on the server. Keyed by the split's form group; null = no rule.
   */
  readonly refreshed = signal<Map<ExpenseReceiptSplitForm, GlAccountRuleAccount | null>>(new Map());
  /** Rule calls in flight (the button spins while any is). */
  readonly refreshing = signal(0);
  private refreshSeq = 0;
  private readonly latestRefresh = new Map<ExpenseReceiptSplitForm, number>();
  /** Each split's cost center code (the rules key on the code; the form holds the id). */
  private readonly costCenterCodes = new Map<ExpenseReceiptSplitForm, string>();

  /**
   * The rate on file (HNL per USD) for the receipt's date — the same figure the API stamps when no rate is typed.
   * Null when there is none on file for that date, or while it loads.
   */
  readonly dayRate = signal<number | null>(null);
  /** The date the rate on file is priced at: the receipt's own date, not today's. */
  readonly rateAsOf = signal<Date | null>(null);
  readonly rateLoading = signal(false);
  readonly rateUnavailable = signal(false);
  /** Override typed by the user; off = the rate on file applies and the input only shows it. */
  readonly rateOverrideOn = signal(false);
  private rateSeq = 0;

  readonly isReview = computed(() => this.mode() === 'review');
  readonly isRejected = computed(() => this.receipt()?.status === ExpenseReceiptStatus.Rejected);

  readonly form: ExpenseReceiptForm = new FormGroup({
    vendor: new FormControl<ExpenseReceiptVendor | null>(null),
    date: new FormControl<Date | null>(null),
    fiscalNumber: new FormControl<string | null>(null),
    taxAuthorizationNumber: new FormControl<string | null>(null),
    currency: new FormControl<Currency>(Currency.HNL, { nonNullable: true }),
    rateToUsd: new FormControl<number | null>(null),
    lines: new FormArray<ExpenseReceiptLineForm>([]),
  });

  constructor() {
    effect(() => {
      const receipt = this.receipt();
      this.openKey();
      const review = this.isReview();
      untracked(() => this.reset(receipt, review));
    });
    // A new date or currency re-prices the receipt; reset() fires both, debounced into one call.
    merge(this.form.controls.date.valueChanges, this.form.controls.currency.valueChanges)
      .pipe(debounceTime(0), takeUntilDestroyed())
      .subscribe(() => this.loadDayRate());
  }

  get lines(): FormArray<ExpenseReceiptLineForm> {
    return this.form.controls.lines;
  }

  private reset(receipt: ExpenseReceipt | null, review: boolean): void {
    this.lines.clear();
    this.costCenterCodes.clear();
    this.refreshed.set(new Map());
    this.latestRefresh.clear();
    this.form.reset({
      vendor: _.isNil(receipt?.vendor)
        ? null
        : { reference: receipt.vendor.reference, code: receipt.vendor.code, name: receipt.vendor.name, taxId: receipt.vendorTaxId },
      // A new receipt starts on today; a saved one keeps its own date (or none).
      date: _.isNil(receipt) ? new Date() : this.fromApiDate(receipt.date),
      fiscalNumber: receipt?.fiscalNumber ?? null,
      taxAuthorizationNumber: receipt?.taxAuthorizationNumber ?? null,
      currency: receipt?.currency ?? this.ownerCurrency() ?? Currency.HNL,
      rateToUsd: receipt?.rateToUsd ?? null,
    });
    this.rateOverrideOn.set(!review && receipt?.rateSource === MANUAL_RATE_SOURCE);
    const lines = receipt?.lines ?? [];
    if (lines.length === 0) {
      this.lines.push(newReceiptLineForm());
    } else {
      lines.forEach((line) => {
        const splits = line.splits.map((split) => {
          const form = newReceiptSplitForm({
            id: split.id,
            costCenterReference: split.costCenter?.reference ?? null,
            total: split.total,
            glAccountOverrideReference: split.glAccountOverride?.reference ?? null,
          });
          if (!_.isNil(split.costCenter?.code)) this.costCenterCodes.set(form, split.costCenter.code);
          return form;
        });
        this.lines.push(
          newReceiptLineForm({ id: line.id, description: line.description, conceptCode: line.concept?.code ?? null, taxRate: line.taxRate }, splits),
        );
      });
    }

    // Review keeps what was approved: who, when, in what currency and how much.
    const locked = [this.form.controls.vendor, this.form.controls.date, this.form.controls.currency, this.form.controls.rateToUsd];
    locked.forEach((control) => (review ? control.disable({ emitEvent: false }) : control.enable({ emitEvent: false })));
    this.allSplits().forEach(({ split }) =>
      review ? split.controls.total.disable({ emitEvent: false }) : split.controls.total.enable({ emitEvent: false }),
    );
    if (!review && !_.isNil(this.ownerCurrency())) this.form.controls.currency.disable({ emitEvent: false });
    // The rate is only typed while overriding; otherwise the input shows the rate on file.
    if (!this.rateOverrideOn()) this.form.controls.rateToUsd.disable({ emitEvent: false });
  }

  /** Unlocks the rate for a typed figure (Manual source on save). */
  onOverrideRate(): void {
    this.rateOverrideOn.set(true);
    this.form.controls.rateToUsd.enable({ emitEvent: false });
  }

  /** Drops the typed rate: the receipt goes back to the rate on file for its date, fetched again. */
  resetToDayRate(): void {
    this.rateOverrideOn.set(false);
    this.form.controls.rateToUsd.disable({ emitEvent: false });
    this.loadDayRate();
  }

  /**
   * Looks up the rate in force on the receipt's date (the average, as the API's converter books it) and shows it in
   * the rate input unless the user is overriding. Review keeps the stamped rate: nothing is re-priced there.
   */
  private loadDayRate(): void {
    const seq = ++this.rateSeq;
    const date = this.form.controls.date.value;
    this.rateAsOf.set(date);
    this.rateUnavailable.set(false);
    if (this.isReview() || !this.isHnl() || _.isNil(date)) {
      this.dayRate.set(null);
      this.rateLoading.set(false);
      return;
    }
    this.rateLoading.set(true);
    this.exchangeRateProxy
      .getRateInForce(Currency.HNL, date)
      .pipe(finalize(() => seq === this.rateSeq && this.rateLoading.set(false)))
      .subscribe({
        next: (response) => {
          if (seq !== this.rateSeq) return;
          const rate = response.data?.averageRateToUSD ?? response.data?.rateToUSD ?? null;
          this.dayRate.set(rate);
          this.rateUnavailable.set(_.isNil(rate));
          if (!this.rateOverrideOn()) this.form.controls.rateToUsd.setValue(rate, { emitEvent: false });
        },
        // Nothing on or before that date: reported on the field, not toasted (the interceptor already does).
        error: () => {
          if (seq !== this.rateSeq) return;
          this.dayRate.set(null);
          this.rateUnavailable.set(true);
          if (!this.rateOverrideOn()) this.form.controls.rateToUsd.setValue(null, { emitEvent: false });
        },
      });
  }

  searchVendors(event: AutoCompleteCompleteEvent): void {
    this.proxy.getVendors(event.query ?? '').subscribe((response) => this.vendorSuggestions.set(response.data ?? []));
  }

  addLine(): void {
    this.lines.push(newReceiptLineForm());
  }

  removeLine(index: number): void {
    this.lines.at(index).controls.splits.controls.forEach((split) => this.costCenterCodes.delete(split));
    this.lines.removeAt(index);
  }

  /** Charges part of the line to another cost center (Option A); it starts empty. */
  addSplit(line: ExpenseReceiptLineForm): void {
    line.controls.splits.push(newReceiptSplitForm());
  }

  /** Drops one of a line's cost centers; the first one stays (a line always has one). */
  removeSplit(line: ExpenseReceiptLineForm, index: number): void {
    if (line.controls.splits.length <= 1) return;
    this.costCenterCodes.delete(line.controls.splits.at(index));
    line.controls.splits.removeAt(index);
  }

  splitsOf(line: ExpenseReceiptLineForm): ExpenseReceiptSplitForm[] {
    return line.controls.splits.controls;
  }

  /** The line's ISV-included total: the sum of its cost centers. */
  lineTotal(line: ExpenseReceiptLineForm): number {
    return _.sumBy(line.controls.splits.getRawValue(), (s) => s.total ?? 0);
  }

  /** In review, a new cost center re-asks the rules for that split's GL account (nothing saved). */
  onCostCenterChange(line: ExpenseReceiptLineForm, split: ExpenseReceiptSplitForm, center: CostCenter | null): void {
    if (_.isNil(center)) this.costCenterCodes.delete(split);
    else this.costCenterCodes.set(split, center.code);
    if (this.isReview()) this.refreshSplits([{ line, split }]);
  }

  /** In review, a new concept re-asks the rules for every cost center of the line (nothing saved). */
  onConceptChange(line: ExpenseReceiptLineForm): void {
    if (this.isReview()) this.refreshSplits(this.splitsOf(line).map((split) => ({ line, split })));
  }

  /** One rules call for every cost center of every line, with what is on screen now. Saves nothing. */
  refreshGlAccounts(): void {
    this.refreshSplits(this.allSplits());
  }

  private allSplits(): { line: ExpenseReceiptLineForm; split: ExpenseReceiptSplitForm }[] {
    return this.lines.controls.flatMap((line) => this.splitsOf(line).map((split) => ({ line, split })));
  }

  /**
   * Asks the GL account rules, in one call, for the given cost centers with their line's concept on screen, and
   * shows the answer on those only. One missing either shows "no rule". Only the latest answer per split counts, so
   * a slow reply never overwrites a newer pick.
   */
  private refreshSplits(targets: readonly { line: ExpenseReceiptLineForm; split: ExpenseReceiptSplitForm }[]): void {
    const company = this.receipt()?.financialCompanyReference;
    if (_.isNil(company) || targets.length === 0) return;
    const seq = ++this.refreshSeq;
    targets.forEach(({ split }) => this.latestRefresh.set(split, seq));
    const rows = targets.map(({ line, split }, index) => ({
      split,
      key: String(index),
      conceptCode: line.controls.conceptCode.value,
      costCenterCode: this.costCenterCodes.get(split) ?? null,
    }));
    const resolvable = rows.filter((r) => !_.isNil(r.conceptCode) && !_.isNil(r.costCenterCode));
    const apply = (answers: Map<string, GlAccountRuleAccount | null>): void => {
      const next = new Map(this.refreshed());
      rows.filter((r) => this.latestRefresh.get(r.split) === seq).forEach((r) => next.set(r.split, answers.get(r.key) ?? null));
      this.refreshed.set(next);
    };
    if (resolvable.length === 0) {
      apply(new Map());
      return;
    }
    this.refreshing.update((n) => n + 1);
    this.ruleProxy
      .resolve({
        scope: GlAccountRuleScope.ExpenseClaim,
        role: GlAccountRuleRole.Expense,
        financialCompanyReference: company,
        lines: resolvable.map((r) => ({
          key: r.key,
          conceptCode: r.conceptCode,
          costCenterCode: r.costCenterCode,
          inventoryTypeReference: null,
        })),
      })
      .pipe(finalize(() => this.refreshing.update((n) => n - 1)))
      .subscribe((response) => apply(new Map((response.data ?? []).map((r) => [r.key, r.glAccount]))));
  }

  /** Whether the split's account shown is a refresh not saved yet. */
  isRefreshed(split: ExpenseReceiptSplitForm): boolean {
    return this.refreshed().has(split);
  }

  /** The saved split behind a form row, for its resolved account. */
  private savedSplit(split: ExpenseReceiptSplitForm): ExpenseReceiptSplit | null {
    const id = split.controls.id.value;
    if (_.isNil(id)) return null;
    return this.receipt()?.lines.flatMap((l) => l.splits).find((s) => s.id === id) ?? null;
  }

  /** The saved cost center of a split, shown by the picker until its tree loads. */
  costCenterOf(split: ExpenseReceiptSplitForm): CostCenterSelection | null {
    const id = split.controls.costCenterReference.value;
    const saved = this.receipt()?.lines.flatMap((l) => l.splits).find((s) => s.costCenter?.reference === id);
    return _.isNil(saved?.costCenter) ? null : { code: saved.costCenter.code, name: saved.costCenter.name };
  }

  /** "6100010001 · Combustible" — what the rules give the split: the last refresh if any, else its last save. */
  ruleAccountOf(split: ExpenseReceiptSplitForm): string | null {
    const account = this.refreshed().has(split) ? this.refreshed().get(split) : this.savedSplit(split)?.resolvedGlAccount;
    if (_.isNil(account?.number)) return null;
    return `${account.number} · ${this.locale.getLocalizedValue(account.name)}`;
  }

  hasOverride(split: ExpenseReceiptSplitForm): boolean {
    return !_.isNil(split.controls.glAccountOverrideReference.value);
  }

  /** HNL receipts may carry a typed rate; USD is always 1. */
  isHnl(): boolean {
    return this.form.controls.currency.value === Currency.HNL;
  }


  /** Subtotal before ISV, ISV and total, the ISV taken out per cost center exactly as the API will. */
  editorTotal(): { subtotal: number; tax: number; total: number } {
    const parts = this.lines.getRawValue().flatMap((l) => l.splits.map((s) => splitTax(s.total ?? 0, l.taxRate)));
    const subtotal = _.sumBy(parts, (s) => s.amount);
    const tax = _.sumBy(parts, (s) => s.tax);
    return { subtotal, tax, total: subtotal + tax };
  }

  save(next = false): void {
    const value = this.form.getRawValue();
    const review = this.isReview();
    const update: ExpenseReceiptUpdate = {
      id: this.receipt()?.id ?? null,
      vendorReference: value.vendor?.reference ?? null,
      fiscalNumber: value.fiscalNumber?.trim() || null,
      taxAuthorizationNumber: value.taxAuthorizationNumber?.trim() || null,
      date: _.isNil(value.date) ? null : this.toApiDate(value.date),
      currency: value.currency,
      // Only a typed rate is sent; null tells the API to stamp the rate on file for the receipt's date.
      rateToUsd: value.currency === Currency.HNL && this.rateOverrideOn() ? value.rateToUsd : null,
      lines: value.lines.map((l) => ({
        id: l.id,
        description: l.description?.trim() || null,
        conceptCode: l.conceptCode,
        taxRate: l.taxRate,
        splits: l.splits.map((s) => ({
          id: s.id,
          costCenterReference: s.costCenterReference,
          total: s.total ?? 0,
          glAccountOverrideReference: review ? s.glAccountOverrideReference : null,
        })),
      })),
    };
    this.saved.emit({ update, next: next && !review });
  }

  cancel(): void {
    this.cancelled.emit();
  }

  attachFile(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!_.isNil(file)) this.attach.emit(file);
  }

  openAttachment(url: string): void {
    window.open(url, '_blank', 'noopener');
  }

  conceptLabel(concept: ExpenseConcept): string {
    return `${concept.code} · ${this.locale.getLocalizedValue(concept.name)}`;
  }

  missingLabel(field: string): string {
    // "Line 2: Concept" → "Line 2: <translated Concept>"
    const match = /^Line (\d+): (.+)$/.exec(field);
    if (match) {
      const key = receiptMissingFieldLabels[match[2]];
      return `${this.label('Receipt_Line', 'Line')} ${match[1]}: ${key ? this.label(key, match[2]) : match[2]}`;
    }
    const key = receiptMissingFieldLabels[field];
    return key ? this.label(key, field) : field;
  }

  symbolOf(currency: Currency | null | undefined): string {
    return currencySymbol(currency);
  }

  getLineRow(rowData: unknown): ExpenseReceiptLineForm {
    return rowData as ExpenseReceiptLineForm;
  }

  private toApiDate(date: Date): string {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${date.getFullYear()}-${month}-${day}`;
  }

  private fromApiDate(value: string | null): Date | null {
    if (_.isNil(value)) return null;
    const [year, month, day] = value.substring(0, 10).split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  private label(key: string, fallback: string): string {
    const value = this.translate.instant(key);
    return value === key ? fallback : value;
  }
}
